import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import classes from './DateRangeCalendar.module.css';
import useMediaQuery from 'Hooks/useMediaQuery';
import Button from 'Components/Atoms/Button/Button';
import SelectWithoutBorder from './SelectWithoutBorder';
import DayPicker from 'Components/Molecules/DayPicker/DayPicker';
import { useTranslation } from 'react-i18next';

const today = new Date();

export const RANGE_DATES = {
    from: new Date(2016, 0, 1),
    to: new Date(today.getFullYear() + 1, 11, 31), // 31 december next year
};

export default function DateRangeCalendar({ range: initialRange, onChange }) {
    const [range, setRange] = useState(() => {
        if (!initialRange) return undefined;

        return {
            from: initialRange.dateFrom,
            to: initialRange.dateTo,
        };
    });
    const isMobile = useMediaQuery('(max-width: 768px)');
    const [selectedMonth, setSelectedMonth] = useState(() => initialRange?.dateTo ?? today);
    const [year, setYear] = useState(() => {
        if (initialRange?.dateTo) {
            return initialRange.dateTo.getFullYear();
        }
        return today.getFullYear();
    });

    const { t } = useTranslation('common');

    useEffect(() => {
        if (!initialRange) {
            setRange(undefined);
            return;
        }

        setRange({
            from: initialRange.dateFrom,
            to: initialRange.dateTo,
        });
    }, [initialRange]);

    const yearOptions = useMemo(() => {
        const years = [];
        const fromYear = RANGE_DATES.from.getFullYear();
        const toYear = RANGE_DATES.to.getFullYear();

        for (let i = fromYear; i <= toYear; i++) {
            years.push({ id: i, label: String(i) });
        }
        return years;
    }, []);

    const handleChange = function handleChange(e) {
        setYear(e.target.value);
        const month = selectedMonth.getMonth();
        setSelectedMonth(new Date(e.target.value, month));
    };

    const handleMonthChange = (month) => {
        setSelectedMonth(month);
        setYear(month.getFullYear());
    };

    const handleApply = () => {
        if (range?.from || range?.to) {
            onChange({
                dateFrom: range.from ?? range.to,
                dateTo: range.to ?? range.from,
            });
        } else {
            onChange(null);
        }
    };

    return (
        <div className={classes.content}>
            <div className={classes.header}>
                <SelectWithoutBorder options={yearOptions} name="year" onChange={handleChange} value={year} />
            </div>

            <div style={{ minHeight: 332 }}>
                <DayPicker
                    numberOfMonths={isMobile ? 1 : 2}
                    month={selectedMonth}
                    onMonthChange={handleMonthChange}
                    mode="range"
                    selected={range}
                    onSelect={setRange}
                    fixedWeeks={isMobile}
                />
            </div>

            <div className={classes.footer}>
                <Button variant="primary" onClick={handleApply}>
                    {t('apply')}
                </Button>
            </div>
        </div>
    );
}

DateRangeCalendar.propTypes = {
    range: PropTypes.shape({
        dateFrom: PropTypes.instanceOf(Date),
        dateTo: PropTypes.instanceOf(Date),
    }),
    onChange: PropTypes.func.isRequired,
};
